import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

/** UI statuses for Path to Value / Implementation Roadmap (DMD-1170). */
export type RoadmapPhaseStatus = 'todo' | 'in-progress' | 'done';

export interface RoadmapPhase {
  phase: string;
  dur: string;
  desc: string;
  /** Stable id when available — required to persist status updates. */
  milestoneId?: string | null;
  /** Raw status from the API. Absent values default to Todo. */
  status?: string | null;
  /** 0–100 from the API. Null when the API reports no progress for the phase. */
  percentComplete?: number | null;
}

export interface RoadmapRowProps {
  roadmap: RoadmapPhase[];
  /** When set, status badges become editable and persist via this callback. */
  onStatusChange?: (milestoneId: string, status: RoadmapPhaseStatus) => Promise<void> | void;
  /** Milestone ids currently saving (disables their control). */
  savingIds?: ReadonlySet<string>;
}

export const ROADMAP_STATUS_OPTIONS: RoadmapPhaseStatus[] = ['todo', 'in-progress', 'done'];

const STATUS_STYLES: Record<RoadmapPhaseStatus, { badge: string; ring: string; label: string }> = {
  todo: {
    badge: 'bg-[#F5F7FA] text-[#717182] border-[#e5e7eb]',
    ring: 'bg-[#18365E]',
    label: 'Todo',
  },
  'in-progress': {
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    ring: 'bg-[#FF6E4C]',
    label: 'In Progress',
  },
  done: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ring: 'bg-emerald-600',
    label: 'Done',
  },
};

/** Map API / legacy labels → UI status. Empty → Todo (templates are not "unreported"). */
export function normalizeStatus(raw: string | null | undefined): RoadmapPhaseStatus {
  const s = String(raw ?? '').toLowerCase().replace(/_/g, '-').trim();
  if (!s) return 'todo';
  if (s === 'completed' || s === 'complete' || s === 'done') return 'done';
  if (s === 'in-progress' || s === 'in progress' || s === 'active' || s === 'current' || s === 'delayed' || s === 'at-risk' || s === 'at risk') {
    return 'in-progress';
  }
  if (
    s === 'todo' ||
    s === 'to-do' ||
    s === 'to do' ||
    s === 'planned' ||
    s === 'plan' ||
    s === 'not-started' ||
    s === 'not started' ||
    s === 'upcoming' ||
    s === 'pending'
  ) {
    return 'todo';
  }
  return 'todo';
}

/** Values accepted by PATCH /command-center/.../milestones/:id */
export function toApiMilestoneStatus(status: RoadmapPhaseStatus): 'Not Started' | 'In Progress' | 'Complete' {
  if (status === 'done') return 'Complete';
  if (status === 'in-progress') return 'In Progress';
  return 'Not Started';
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function reportedPercent(phase: RoadmapPhase): number | null {
  const pct = phase.percentComplete;
  return typeof pct === 'number' && Number.isFinite(pct) ? clampPct(pct) : null;
}

/**
 * Bar fill for one phase. Uses the API percentage when present, otherwise only the two
 * unambiguous cases. In-progress without a reported percentage has no fill — the UI must not
 * invent a mid-progress value.
 */
function phaseFill(phase: RoadmapPhase, status: RoadmapPhaseStatus): number | null {
  const pct = reportedPercent(phase);
  if (pct != null) return pct;
  if (status === 'done') return 100;
  if (status === 'todo') return 0;
  return null;
}

export function roadmapProgressSummary(roadmap: RoadmapPhase[]): {
  completed: number;
  inProgress: number;
  planned: number;
  unknown: number;
  /** Null when there are no phases. */
  pct: number | null;
  meta: string;
} {
  let completed = 0;
  let inProgress = 0;
  let planned = 0;
  const reported: number[] = [];

  for (const phase of roadmap) {
    const status = normalizeStatus(phase.status);
    if (status === 'done') completed += 1;
    else if (status === 'in-progress') inProgress += 1;
    else planned += 1;

    const pct = reportedPercent(phase);
    if (pct != null) reported.push(pct);
  }

  const total = roadmap.length;
  if (total === 0) {
    return { completed, inProgress, planned, unknown: 0, pct: null, meta: 'No phases' };
  }

  const pct =
    reported.length === total
      ? Math.round(reported.reduce((sum, n) => sum + n, 0) / total)
      : Math.round((completed / total) * 100);

  const meta =
    `${completed}/${total} complete` +
    (inProgress ? ` · ${inProgress} in progress` : '') +
    (planned ? ` · ${planned} todo` : '');

  return { completed, inProgress, planned, unknown: 0, pct, meta };
}

function StatusControl({
  status,
  disabled,
  saving,
  onChange,
}: {
  status: RoadmapPhaseStatus;
  disabled?: boolean;
  saving?: boolean;
  onChange?: (next: RoadmapPhaseStatus) => void;
}) {
  const styles = STATUS_STYLES[status];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!onChange || disabled) {
    return (
      <span className={`inline-flex text-[10px] font-semibold px-1.5 py-0.5 rounded border ${styles.badge}`}>
        {styles.label}
      </span>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status: ${styles.label}`}
        disabled={saving}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${styles.badge} hover:brightness-[0.98] disabled:opacity-60`}
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        <span>{styles.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 min-w-[8.5rem] overflow-hidden rounded-lg border border-[#e5e7eb] bg-white shadow-lg"
        >
          {ROADMAP_STATUS_OPTIONS.map((option) => {
            const opt = STATUS_STYLES[option];
            const selected = option === status;
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  setOpen(false);
                  if (option !== status) onChange(option);
                }}
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[10px] font-semibold transition-colors hover:bg-[#F5F7FA] ${
                  selected ? 'bg-[#F5F7FA]' : ''
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${opt.ring}`} />
                <span className={opt.badge.includes('emerald') ? 'text-emerald-700' : opt.badge.includes('amber') ? 'text-amber-800' : 'text-[#717182]'}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const RoadmapRow: React.FC<RoadmapRowProps> = ({ roadmap, onStatusChange, savingIds }) => {
  if (roadmap.length === 0) {
    return <p className="text-sm text-[#717182] italic">No implementation phases are documented yet.</p>;
  }
  return (
    <div className="space-y-3">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${Math.min(roadmap.length, 4)}, minmax(0, 1fr))` }}
      >
        {roadmap.map((phase, i) => {
          const status = normalizeStatus(phase.status);
          const styles = STATUS_STYLES[status];
          const fill = phaseFill(phase, status);
          const id = phase.milestoneId ?? null;
          const canEdit = Boolean(onStatusChange && id);
          return (
            <div
              key={`${id || phase.phase}-${i}`}
              className="bg-[#F5F7FA] rounded-lg p-3 border border-[#e5e7eb] h-full flex flex-col"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className={`w-5 h-5 rounded-full ${styles.ring} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}
                >
                  {i + 1}
                </span>
                <span className="text-xs font-bold text-[#18365E]">{phase.phase}</span>
              </div>
              <p className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide mb-1">{phase.dur}</p>
              <p className="text-xs text-[#717182] leading-relaxed mb-2">{phase.desc}</p>
              {/* Pin status + progress to card bottom so dropdowns align across unequal copy lengths (DMD-1089). */}
              <div className="mt-auto space-y-1.5 pt-1">
                <StatusControl
                  status={status}
                  saving={Boolean(id && savingIds?.has(id))}
                  onChange={
                    canEdit
                      ? (next) => {
                          void onStatusChange?.(id!, next);
                        }
                      : undefined
                  }
                />
                {fill != null && (
                  <div className="h-1.5 rounded-full bg-white border border-[#e5e7eb] overflow-hidden">
                    <div
                      className={`h-full ${styles.ring} transition-all`}
                      style={{ width: `${fill}%` }}
                      aria-hidden
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const PathToValueList: React.FC<RoadmapRowProps> = ({ roadmap, onStatusChange, savingIds }) => {
  if (roadmap.length === 0) {
    return <p className="text-xs text-[#717182] italic">Timing not yet documented.</p>;
  }
  return (
    <ol className="space-y-2">
      {roadmap.map((phase, i) => {
        const status = normalizeStatus(phase.status);
        const styles = STATUS_STYLES[status];
        const id = phase.milestoneId ?? null;
        const canEdit = Boolean(onStatusChange && id);
        return (
          <li key={`${id || phase.phase}-${i}`} className="flex items-center gap-2.5 text-xs">
            <span
              className={`w-5 h-5 rounded-full ${styles.ring} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}
            >
              {i + 1}
            </span>
            <span className="font-semibold text-[#18365E]">{phase.phase}</span>
            <StatusControl
              status={status}
              saving={Boolean(id && savingIds?.has(id))}
              onChange={
                canEdit
                  ? (next) => {
                      void onStatusChange?.(id!, next);
                    }
                  : undefined
              }
            />
            <span className="text-[#717182] ml-auto">{phase.dur}</span>
          </li>
        );
      })}
    </ol>
  );
};

export default RoadmapRow;
